// client/src/pages/LoginPage.js
import React, { useState, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Card, Typography, TextField, Button, Alert, Stack, Box, InputAdornment, IconButton } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { AuthContext } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const { login } = useContext(AuthContext);
  const { store } = useStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(redirect || '/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '75vh', display: 'grid', placeItems: 'center', py: 8, px: 2,
        background:
          'radial-gradient(700px 400px at 0% 0%, rgba(53,99,233,0.14), transparent 60%),' +
          'radial-gradient(600px 400px at 100% 100%, rgba(255,140,80,0.14), transparent 60%)',
      }}
    >
      <Container maxWidth="xs" disableGutters>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 800, letterSpacing: '0.14em' }}>
          Welcome back
        </Typography>
        <Typography variant="h3" fontWeight={800} letterSpacing="-0.02em" sx={{ fontSize: '2.2rem', mb: 3 }}>
          Sign in to {store.name}
        </Typography>
        <Card sx={{ p: { xs: 3, sm: 4 }, borderRadius: 5 }}>
          <Stack component="form" onSubmit={handleSubmit} spacing={2.25}>
            {error && <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>}
            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth autoComplete="email" />
            <TextField
              label="Password" type={showPassword ? 'text' : 'password'} value={password}
              onChange={(e) => setPassword(e.target.value)} required fullWidth autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" aria-label="Toggle password visibility">
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ py: 1.5, fontWeight: 700 }}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>
        </Card>
        <Typography variant="body2" textAlign="center" sx={{ mt: 3, color: 'text.secondary' }}>
          New here?{' '}
          <Link to={`/register${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`} style={{ color: 'inherit', fontWeight: 700 }}>
            Create an account
          </Link>
        </Typography>
      </Container>
    </Box>
  );
};

export default LoginPage;
